/* eslint-disable react-hooks/exhaustive-deps */
import { IGResponse, IRoute, Locality, TripType } from "@common/models";
import { ICity } from "@common/models/CMS";
import { Card } from "@components/general/card/Card";
import { CardContent } from "@components/general/card/CardContent";
import Panel from "@components/general/Panel";
import { Weather } from "@components/weather";
import WeeklyFlightAvailability from "@components/general/WeeklyFlightAvailability";
import FlightSearchTabPanel from "@components/home/FlightSearchTabPanel";
import WidgetContainer from "@components/Widgets/WidgetContainer";
import { getFilePath, IViewport } from "@config/appConfig";
import { AppAction, IAppAction } from "@misc/Action";
import { IAppState } from "@misc/State";
import { PageMeta } from "@pages/page";
import Config from "@service/Config";
import { CITIES_INFORMATION, useApolloQuery } from "@service/graphql";
import Translate from "@service/Translate";
import { getAirport, getAirportByCityName, getAirportsByCityName } from "@utilize/Format";
import { setOriginCityName } from "@utilize/Geo";
import { FC, useEffect, useMemo, useState } from "react";
import ContentLoader from "react-content-loader";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router";

interface ICityParams { id: string }

const CityInformation: FC = () => {
    const { small } = Config.get<IViewport>("viewport");
    const { tr } = Translate;
    const { id } = useParams<ICityParams>();
    const dispatch = useDispatch();
    const [orgCode, setOrgCode] = useState<string>();
    const query = new URLSearchParams(useLocation().search);
    let { availabilityRequest, routes, width } = useSelector((state: IAppState) => ({
        availabilityRequest: state.availabilityRequest,
        routes: state.routes,
        width: state.windowWidth
    }));
    const { data, loading } = useApolloQuery<IGResponse<ICity[]>>(CITIES_INFORMATION, { variables: { code: id } });
    const cityData = useMemo(() => data?.city[0], [data]);

    useEffect(() => {
        setAvailabilityRequest();
    }, [data, routes])

    useEffect(() => {
        return (() => {
            dispatch<IAppAction>({
                type: AppAction.AvailabilityRequest, payload: {
                    availabilityRequest: {
                        originDestionations: [{}],
                        paxTypeQuantity: {
                            adt: 1,
                            chd: 0,
                            inf: 0,
                            yad: 0
                        },
                        tripType: TripType.OneWay
                    }
                }
            });
        })
    }, []);

    const setAvailabilityRequest = (): void => {
        if (availabilityRequest.originDestionations) {
            const baseCity = query.get('baseCity');
            if (baseCity) {
                const route = setOriginCityNameOfBaseCityByLocality(routes, baseCity, id);
                availabilityRequest.originDestionations[0].originCode = route.origin;
                availabilityRequest.originDestionations[0].destinationCode = id;
                setOrgCode(route.origin);
                dispatch<IAppAction>({ type: AppAction.AvailabilityRequest, payload: { availabilityRequest } });
            } else {
                setOriginCityName()
                    .then(cityName => {
                        if (availabilityRequest.originDestionations) {
                            const selectedRoutes = getRoute(routes, { desCode: id }) as IRoute[];
                            const originCode = selectedRoutes.length === 1 ? selectedRoutes[0].origin : getAirportByCityName(cityName)?.code || setDefaultCodeDestinationCity();
                            availabilityRequest.originDestionations[0].originCode = originCode;
                            availabilityRequest.originDestionations[0].destinationCode = id;
                            setOrgCode(originCode);
                        }
                        dispatch<IAppAction>({ type: AppAction.AvailabilityRequest, payload: { availabilityRequest } });
                    })
                    .catch(err => {
                        console.log(err);
                    });
            }
        };
    };

    const setDefaultCodeDestinationCity = (): string => {
        return (getRoute(routes, { desCode: id }) as IRoute[]).every(route => route.locality === Locality.International) ? "IKA" : "THR"
    }

    return (
        <PageMeta title={cityData?.displayText} description={cityData?.displayText} >
            <main className="city">
                <header className="header-page" style={{ backgroundImage: `url(${getFilePath(cityData?.cover?.paths[0], true)})` }}>
                    <article className="header-article container">
                        <h1 className="title-header">{loading ? setLoaderElement({ height: '30px', width: '50%' }) : `${tr("city_flights_to", cityData?.name ? cityData?.name : '')} (${id})`}</h1>
                        <h2 className="sub-title-header">{loading ? setLoaderElement({ height: '30px', width: '25%' }) : cityData?.nickname}</h2>
                        {width > small && <Weather
                            city={cityData}
                            daysCount={width < small ? 3 : 5}
                        />}
                    </article>
                </header>
                <div className="article container">
                    <Panel>
                        <section className="header-panel">
                            <h2 className="title-panel">{tr("city_reserved_just_now", getAirport(id).cityName, '!')}</h2>
                            <article className="weekly-flight-container">
                                <h4 className="title-weekly-flights">{tr("rd_indicate_weekly_flights")}</h4>
                                <WeeklyFlightAvailability
                                    route={getRoute(routes, { desCode: id, orgCode: orgCode }) as IRoute}
                                    destinationVisiblity={false}
                                />
                            </article>
                        </section>
                        <FlightSearchTabPanel />
                    </Panel>
                    <section className="city-information">
                        {width <= small &&
                            <Card variant="horizontal" className="weather-mobile">
                                <CardContent>
                                    <Weather
                                        city={cityData}
                                        daysCount={width < small ? 3 : 5}
                                    />
                                </CardContent>
                            </Card>
                        }
                        <Card variant="horizontal">
                            <CardContent mediaBackground={{ icon: "icon-area" }}>
                                <h3 className="title">{tr("ground_area")}</h3>
                                <p className="desc area">{cityData?.area} Km<sup>2</sup></p>
                            </CardContent>
                        </Card>
                        <Card variant="horizontal">
                            <CardContent mediaBackground={{ icon: "icon-population" }}>
                                <h3 className="title">{tr("population")}</h3>
                                <p className="desc">{cityData?.population?.toLocaleString()} {tr("units_member")}</p>
                            </CardContent>
                        </Card>
                        <Card variant="horizontal">
                            <CardContent mediaBackground={{ icon: "icon-weather" }}>
                                <h3 className="title">{tr("climate")}</h3>
                                <p className="desc">{cityData?.climate}</p>
                            </CardContent>
                        </Card>
                        <Card variant="horizontal">
                            <CardContent mediaBackground={{ icon: "icon-altitude" }}>
                                <h3 className="title">{tr("c_level")}</h3>
                                <p className="desc">{cityData?.level} {tr("units_m")}</p>
                            </CardContent>
                        </Card>
                    </section>
                    <WidgetContainer
                        generalContainer={cityData}
                    />
                </div>
            </main>
        </PageMeta>
    )
}


export const getRoute = (routes: IRoute[], code: { orgCode?: string, desCode: string }): IRoute | IRoute[] => {
    const route = code.orgCode ? routes.find(route => (route.origin === code.orgCode) && (route.destination === code.desCode)) || {} :
        routes.filter(route => route.destination === code.desCode) || {};
    return route;
}


export const setLoaderElement = (dimension: { height: string, width: string }): JSX.Element => {
    const loaderColor = {
        foregroundColor: '#8686866e',
        backgroundColor: '#ffffff3d'
    }
    return <ContentLoader width={dimension.width}
        height={dimension.height}
        // title={tr("msg_availability_flights_loading")}
        speed={1.4}
        backgroundColor={loaderColor.backgroundColor}
        foregroundColor={loaderColor.foregroundColor}
    // rtl={lang === IrLocale.lang}
    >
        <rect x="0" y="0" rx="0" ry="0" width="100%" height="100%" />
    </ContentLoader>
}

const setOriginCityNameOfBaseCityByLocality = (routes: IRoute[], originCode: string, destinationCode: string): IRoute => {
    const cityName = getAirport(originCode).cityName;
    const airports = getAirportsByCityName(cityName);
    let route: IRoute = {};
    for (let airport of airports) {
        const selectedRoute = getRoute(routes, { orgCode: airport.code, desCode: destinationCode }) as IRoute;
        if (selectedRoute.origin)
            route = selectedRoute;
    }
    return route;
}

export default CityInformation